import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link as LinkIcon, Plus, Trash2, ExternalLink } from 'lucide-react';
import Button from '../ui/Button';
import Input from '../ui/Input';

const ExternalLinksEditor = ({ links = [], onChange, readOnly = false }) => {
  const [localLinks, setLocalLinks] = useState(links || []);

  const handleAddLink = () => {
    const newLinks = [...localLinks, { label: '', url: '' }];
    setLocalLinks(newLinks);
    if (onChange) onChange(newLinks);
  };

  const handleUpdateLink = (index, field, value) => {
    const newLinks = [...localLinks];
    newLinks[index][field] = value;
    setLocalLinks(newLinks);
    if (onChange) onChange(newLinks);
  };

  const handleRemoveLink = (index) => {
    const newLinks = localLinks.filter((_, i) => i !== index);
    setLocalLinks(newLinks);
    if (onChange) onChange(newLinks);
  };

  const isValidUrl = (url) => {
    try {
      new URL(url);
      return true;
    } catch {
      return false;
    }
  };

  if (readOnly) {
    if (!localLinks || localLinks.length === 0) return null;

    return (
      <div className="space-y-2">
        <div className="flex items-center space-x-2 mb-3">
          <ExternalLink className="h-4 w-4 text-muted-foreground" />
          <h4 className="text-sm font-medium text-foreground">External Links</h4>
        </div>
        <div className="space-y-2">
          {localLinks.map((link, index) => (
            link.label && link.url ? (
              <a
                key={index}
                href={link.url}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center space-x-2 text-sm text-primary hover:underline p-2 rounded bg-white/5 border border-white/10"
              >
                <LinkIcon className="h-3 w-3" />
                <span>{link.label}</span>
                <ExternalLink className="h-3 w-3" />
              </a>
            ) : null
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <label className="block text-sm font-medium mb-2">
          External Links
        </label>
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={handleAddLink}
          className="flex items-center space-x-2"
        >
          <Plus className="h-4 w-4" />
          <span>Add Link</span>
        </Button>
      </div>

      <AnimatePresence>
        {localLinks.map((link, index) => (
          <motion.div
            key={index}
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="grid grid-cols-1 md:grid-cols-5 gap-2 items-end p-3 bg-white/5 rounded-lg border border-white/10"
          >
            <div className="md:col-span-2">
              <label className="block text-xs text-muted-foreground mb-1">Label</label>
              <Input
                value={link.label || ''}
                onChange={(e) => handleUpdateLink(index, 'label', e.target.value)}
                placeholder="e.g., Support Page"
              />
            </div>
            <div className="md:col-span-2">
              <label className="block text-xs text-muted-foreground mb-1">URL</label>
              <Input
                type="url"
                value={link.url || ''}
                onChange={(e) => handleUpdateLink(index, 'url', e.target.value)}
                placeholder="https://example.com"
                className={link.url && !isValidUrl(link.url) ? 'border-destructive' : ''}
              />
            </div>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => handleRemoveLink(index)}
              className="text-destructive hover:bg-destructive/10"
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          </motion.div>
        ))}
      </AnimatePresence>

      {localLinks.length === 0 && (
        <div className="text-center py-4 text-sm text-muted-foreground">
          No links added. Click "Add Link" to add one.
        </div>
      )}
    </div>
  );
};

export default ExternalLinksEditor;

